import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import {
  buildConversationList,
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
  isOnFoldersView,
  setContacts,
} from './helpers/actionHelpers';
import {
  cancelVisibleListRefresh,
  scheduleVisibleListRefresh,
} from './helpers/visibleListRefresh';
import {
  conversationNeedsKanbanHydrate,
  mergeKanbanItemsMembership,
  resolveMessageConversationActivityAt,
  buildMessagingWindowPatchFromMessage,
} from './helpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import * as Sentry from '@sentry/vue';
import {
  handleVoiceCallCreated,
  handleVoiceCallUpdated,
  syncConversationCallVisibility,
} from 'dashboard/helper/voice';

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

import messageForwardActions from './actions/messageForwardActions';

const beginListRequest = ({ commit, state }) => {
  commit(types.SET_LIST_LOADING_STATUS);
  const requestId = (state.listRequestId || 0) + 1;
  commit(types.SET_LIST_REQUEST_ID, requestId);
  return requestId;
};

// Older tests and third-party store harnesses may omit this new state key. In
// the real Vuex module it is always present and makes the newest request win.
const isCurrentListRequest = (state, requestId) =>
  state.listRequestId === undefined || state.listRequestId === requestId;

const conversationExistsInList = (state, conversationId) =>
  (state?.allConversations || []).some(chat => chat.id === conversationId);

// Resolve the same filter/custom-view query ReconnectService uses so recovery
// re-hits ConversationFinder with the currently visible list semantics.
const resolveVisibleListFilterQuery = (getters, rootGetters) => {
  const appliedQuery = getters?.getAppliedConversationFiltersQuery;
  if (appliedQuery?.payload?.length) return appliedQuery;

  const activeFolder = rootGetters?.['customViews/getActiveConversationFolder'];
  if (activeFolder?.query?.payload?.length) return activeFolder.query;

  return null;
};

const hasVisibleListContext = (state, getters, rootGetters) => {
  if (resolveVisibleListFilterQuery(getters, rootGetters)) return true;
  const filters = state.conversationFilters || {};
  return Object.keys(filters).length > 0;
};

// actions
const actions = {
  getConversation: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  fetchAllConversations: async ({ commit, state, dispatch }) => {
    const requestId = beginListRequest({ commit, state });
    try {
      const params = { ...state.conversationFilters };
      const {
        data: { data },
      } = await ConversationApi.get(params);
      if (!isCurrentListRequest(state, requestId)) return;

      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.assigneeType
      );
    } catch (error) {
      // Handle error
      if (isCurrentListRequest(state, requestId)) {
        commit(types.CLEAR_LIST_LOADING_STATUS);
      }
    }
  },

  fetchFilteredConversations: async (
    { commit, dispatch, state = {} },
    params
  ) => {
    const requestId = beginListRequest({ commit, state });
    try {
      const { data } = await ConversationApi.filter(params);
      if (!isCurrentListRequest(state, requestId)) return;

      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
    } catch (error) {
      // Handle error
      if (isCurrentListRequest(state, requestId)) {
        commit(types.CLEAR_LIST_LOADING_STATUS);
      }
    }
  },

  emptyAllConversations({ commit }) {
    // Drop any pending recovery so a filter/route reset cannot merge stale page-1.
    cancelVisibleListRefresh();
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      if (!payload.length) {
        commit(types.SET_ALL_MESSAGES_LOADED, data.conversationId);
      }
    } catch (error) {
      // Handle error
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = [];

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data.payload;
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch }, { data, after }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED, data.id);
    // Membership list/push only ships slim kanban_items. Hydrate full payload
    // for ContactPanel when missing or slim so the panel is never blocked.
    dispatch('hydrateConversationKanbanItemsIfNeeded', data?.id);
    if (data.dataFetched === undefined) {
      try {
        await dispatch('fetchPreviousMessages', {
          after,
          before: data.messages[0].id,
          conversationId: data.id,
        });
        commit(types.SET_CHAT_DATA_FETCHED, data.id);
      } catch (error) {
        // Ignore error
      }
    }
  },

  // Full kanban_items for the open conversation. Slim membership payloads only
  // carry { id, funnel_id, funnel_stage } and must not count as "already loaded".
  // Dynamic import avoids KanbanAPI → store → conversations circular load.
  async hydrateConversationKanbanItemsIfNeeded(
    { state, commit },
    conversationId
  ) {
    if (!conversationId) return;
    const chat = state.allConversations.find(
      c => String(c.id) === String(conversationId)
    );
    if (!chat || !conversationNeedsKanbanHydrate(chat.kanban_items)) return;

    try {
      const { default: KanbanAPI } = await import('../../../api/kanban');
      const { data } = await KanbanAPI.getItemsByConversation(conversationId);
      const items = data?.items ?? [];
      if (!items.length) return;

      const sorted = [...items].sort(
        (a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0)
      );

      const latest = state.allConversations.find(
        c => String(c.id) === String(conversationId)
      );
      if (!latest || !conversationNeedsKanbanHydrate(latest.kanban_items)) {
        return;
      }

      commit(types.UPDATE_CONVERSATION_KANBAN_ITEMS, {
        conversationId: latest.id,
        kanbanItems: mergeKanbanItemsMembership(latest.kanban_items, sorted),
      });
    } catch {
      // Conversation may not have a kanban card.
    }
  },

  assignAgent: async ({ dispatch }, { conversationId, agentId }) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
      });
      dispatch('setCurrentChatAssignee', {
        conversationId,
        assignee: response.data,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatAssignee({ commit }, { conversationId, assignee }) {
    commit(types.ASSIGN_AGENT, { conversationId, assignee });
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null, customAttributes = null }
  ) => {
    try {
      // Update custom attributes first if provided
      if (customAttributes) {
        await ConversationApi.updateCustomAttributes({
          conversationId,
          customAttributes,
        });
        commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
          conversationId,
          customAttributes,
        });
      }

      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    // Await/return so ReplyBox and other callers see real success/failure
    // (e.g. 422) instead of a cosmetic resolved promise.
    return dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      // Server-success only: bump list activity so last_activity_desc reorders
      // immediately without waiting for ActionCable. Failure path never reaches here.
      const lastActivityAt = resolveMessageConversationActivityAt(
        response.data
      );
      if (lastActivityAt != null) {
        commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
          lastActivityAt,
          conversationId,
        });
      }
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  addMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    const messagingWindowPatch = buildMessagingWindowPatchFromMessage(message);
    if (messagingWindowPatch) {
      commit(types.SET_CONVERSATION_CAN_REPLY, messagingWindowPatch);
    }
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
    handleVoiceCallCreated(
      message,
      rootGetters?.getCurrentUserID,
      rootGetters?.getCurrentUserAvailability
    );
  },

  updateMessage({ commit, rootGetters }, message) {
    commit(types.UPDATE_MESSAGE, message);
    handleVoiceCallUpdated(
      commit,
      message,
      rootGetters?.getCurrentUserID,
      rootGetters?.getCurrentUserAvailability
    );
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  // Edita conteudo da mensagem (Channel::Api WAHA, 15min). Backend chama WAHA,
  // se falhar reverte conteudo local — entao o erro pode chegar aqui via reject.
  editMessage: async ({ commit }, { conversationId, messageId, content }) => {
    const { data } = await MessageApi.editContent(
      conversationId,
      messageId,
      content
    );
    commit(types.ADD_MESSAGE, data);
    return data;
  },

  deleteConversation: async ({ commit, dispatch }, conversationId) => {
    try {
      await ConversationApi.delete(conversationId);
      commit(types.DELETE_CONVERSATION, conversationId);
      dispatch('conversationStats/get', {}, { root: true });
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    if (
      !hasAppliedFilters &&
      !isOnFoldersView(rootState) &&
      !isOnMentionsView(rootState) &&
      !isOnParticipatingView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, dispatch, state, rootGetters }, conversation) {
    const sender = conversation.meta?.sender;

    commit(types.UPDATE_CONVERSATION, conversation);
    syncConversationCallVisibility(conversation, rootGetters?.getCurrentUserID);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    if (sender) dispatch('contacts/setContact', sender);

    // After slim membership merge, still hydrate full payload for the open panel.
    if (
      conversation?.id != null &&
      state &&
      String(state.selectedChatId) === String(conversation.id)
    ) {
      dispatch('hydrateConversationKanbanItemsIfNeeded', conversation.id);
    }
  },

  updateConversationLastActivity(
    { commit, state, dispatch },
    { conversationId, lastActivityAt }
  ) {
    // Cheap path: conversation already loaded — monotonic reorder only.
    if (conversationExistsInList(state, conversationId)) {
      commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
        lastActivityAt,
        conversationId,
      });
      return;
    }

    // Absent from the current Vuex list: do not invent a row. Coalesce a
    // filter-aware page-1 refresh so ConversationFinder decides membership.
    dispatch('scheduleVisibleConversationListRefresh');
  },

  // Throttled scheduler used when realtime events name a conversation that is
  // not present in allConversations. Account-scoped; trailing-debounce.
  scheduleVisibleConversationListRefresh({
    dispatch,
    state,
    getters,
    rootGetters,
  }) {
    const accountId = rootGetters?.getCurrentAccountId;
    if (accountId == null) return;
    if (!hasVisibleListContext(state, getters, rootGetters)) return;

    scheduleVisibleListRefresh({
      accountId,
      run: () => {
        // Re-check at fire time so a timer from account A never refreshes B.
        if (rootGetters?.getCurrentAccountId !== accountId) return;
        if (!hasVisibleListContext(state, getters, rootGetters)) return;
        dispatch('refreshVisibleConversationList', { accountId });
      },
    });
  },

  // Filter-aware recovery refresh. Merges page 1 of the current list context
  // without emptying the cache, resetting filters, or flipping load-more page.
  //
  // Must never bump listRequestId / SET_LIST_LOADING_STATUS: stealing the
  // request generation from an in-flight fetchAllConversations leaves the
  // legitimate spinner stuck (isCurrentListRequest fails → no CLEAR).
  refreshVisibleConversationList: async (
    { commit, state, getters, rootGetters, dispatch },
    { accountId } = {}
  ) => {
    const currentAccountId = rootGetters?.getCurrentAccountId;
    if (accountId != null && currentAccountId !== accountId) return;
    if (!hasVisibleListContext(state, getters, rootGetters)) return;

    // Avoid racing a legitimate list fetch. Re-schedule trailing recovery so
    // we retry after loading settles, without invalidating the fetch's id.
    if (state.listLoadingStatus) {
      dispatch('scheduleVisibleConversationListRefresh');
      return;
    }

    // Snapshot generation only — recovery is observer, not owner of listRequestId.
    const startedAtRequestId = state.listRequestId;
    const startedAccountId = accountId ?? currentAccountId;

    try {
      const filterQuery = resolveVisibleListFilterQuery(getters, rootGetters);
      let conversationList = [];
      let metaData = {};

      if (filterQuery) {
        const { data } = await ConversationApi.filter({
          queryData: filterQuery,
          page: 1,
          sortBy: state.conversationFilters?.sortBy,
          adminAssigneeScope: state.conversationFilters?.adminAssigneeScope,
        });
        conversationList = data?.payload || [];
        metaData = data?.meta || {};
      } else {
        const params = { ...state.conversationFilters, page: 1 };
        const {
          data: { data },
        } = await ConversationApi.get(params);
        conversationList = data?.payload || [];
        metaData = data?.meta || {};
      }

      // Drop stale recovery if a real list request advanced the generation or
      // the agent switched account while the recovery HTTP call was in flight.
      if (state.listRequestId !== startedAtRequestId) return;
      if (
        startedAccountId != null &&
        rootGetters?.getCurrentAccountId !== startedAccountId
      ) {
        return;
      }

      commit(types.SET_ALL_CONVERSATION, conversationList);
      dispatch('conversationStats/set', metaData);
      dispatch(
        'conversationLabels/setBulkConversationLabels',
        conversationList
      );
      // Keep contacts in sync with newly merged rows (same as buildConversationList).
      // Note (P3): SET_ALL_CONVERSATION merges without prune — rows that left the
      // filter may remain cached until a full fetch rebuilds the list.
      setContacts(commit, conversationList);
    } catch (error) {
      // Recovery is best-effort; leave the current list intact on failure.
    }
  },

  cancelVisibleConversationListRefresh() {
    cancelVisibleListRefresh();
  },

  // Patch a conversation's kanban_items array in-place. Called from the
  // real-time kanban broadcast so the conversation side panel reflects
  // changes immediately. conversationId is the conversation display_id.
  updateConversationKanbanItems({ commit }, { conversationId, kanbanItems }) {
    if (!conversationId) return;
    commit(types.UPDATE_CONVERSATION_KANBAN_ITEMS, {
      conversationId,
      kanbanItems: kanbanItems || [],
    });
  },

  // Immediate membership patch after participant create/update/destroy so
  // Mine (assignee|participant) and Unassigned (no owner) refilter without F5.
  updateConversationParticipantUserIds(
    { commit },
    { conversationId, participantUserIds }
  ) {
    if (!conversationId) return;
    commit(types.UPDATE_CONVERSATION_PARTICIPANT_USER_IDS, {
      conversationId,
      participantUserIds: participantUserIds || [],
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationInbox({ commit }, { conversationIds, inboxId }) {
    const ids = Array.isArray(conversationIds)
      ? conversationIds
      : [conversationIds];
    ids.forEach(conversationId => {
      commit(types.UPDATE_CONVERSATION_INBOX, { conversationId, inboxId });
    });
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    await ConversationApi.sendEmailTranscript({ conversationId, email });
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
        conversationId,
        customAttributes: custom_attributes,
      });
    } catch (error) {
      // Handle error
    }
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  getInboxCaptainAssistantById: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.getInboxAssistant(conversationId);
      commit(types.SET_INBOX_CAPTAIN_ASSISTANT, response.data);
    } catch (error) {
      // Handle error
    }
  },

  ...messageReadActions,
  ...messageTranslateActions,
  ...messageForwardActions,
};

export default actions;
